import React, { DetailedHTMLProps, ForwardedRef, InputHTMLAttributes } from "react";
import theme from "@monorepo/styles/theme";
import { IResponseError } from "@monorepo/types";
import { Box, styled } from "@mui/material";
import { SerializedError } from "@reduxjs/toolkit";

import { InputError } from "../Input/Input";

const checkboxWidth = 22;
const checkboxPaddingLeft = checkboxWidth + 6; // plus spacing-x-small;
const checkboxBorderRadius = 3;

export const SInput = styled("input")(() => ({
  position: "absolute",
  opacity: 0,
  cursor: "pointer",
  height: 0,
  width: 0,
  "&:checked": {
    "~ .checkmark": {
      "&:after": {
        display: "block",
      },
    },
  },
  "&:disabled": {
    "~ .checkmark": {
      backgroundColor: theme.palette.nobelGray.main,
      borderColor: `darken(${theme.palette.nobelGray.main}, 2%)`,
      "&:after": {
        borderColor: `darken(${theme.palette.nobelGray.main}, 30%)`,
      },
      "&:hover": {
        borderColor: `darken(${theme.palette.nobelGray.main}, 30%)`,
      },
    },
  },
  "&:focus-visible": {
    "~ .checkmark, ~ .container": {
      marginLeft: `${theme.spacing(2 / 8)}`,
      outline: `${theme.spacing(2 / 8)} solid ${theme.palette.primary.main}`,
    },
  },
}));

type TProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  label?: React.ReactNode;
  error?: string | Array<IResponseError> | SerializedError;
};

export const Checkbox = React.forwardRef((props: TProps, ref: ForwardedRef<HTMLInputElement>) => {
  const { label, id, error, ...otherProps } = props;

  return (
    <>
      <Box
        component="label"
        sx={{
          display: "block",
          position: "relative",
          paddingLeft: `${checkboxPaddingLeft}px`,
          cursor: "pointer",
          appearance: "none",
          "&:hover": {
            "> .checkbox": {
              "&:disabled": {
                "~ .checkmark": {
                  borderColor: `darken(${theme.palette.nobelGray.main}, 2%)`,
                },
              },
              "~ .checkmark": {
                borderColor: `darken(${theme.palette.text.secondary}, 50%)`,
              },
            },
          },
        }}
        className="container"
      >
        <SInput type="checkbox" id={id} ref={ref} {...otherProps} />
        <Box
          component="span"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: `${checkboxWidth}px`,
            width: `${checkboxWidth}px`,
            borderRadius: `${checkboxBorderRadius}px`,
            border: `${theme.spacing(2 / 8)} solid ${theme.palette.shuttleGray.main}`,
            backgroundColor: "transparent",
            "&:after": {
              content: "''",
              position: "absolute",
              display: "none",
              left: "5px",
              top: "4px",
              width: "9px",
              height: "8px",
              border: `solid 1px ${theme.palette.shuttleGray.main}`,
              borderWidth: `0px ${theme.spacing(2 / 8)} ${theme.spacing(2 / 8)} 0px`,
              transform: "rotate(45deg)",
            },
          }}
          className="checkmark"
        ></Box>
        {label}
      </Box>
      {error && (
        <InputError
          sx={{
            marginLeft: "34px",
          }}
        >
          {error}
        </InputError>
      )}
    </>
  );
});
